import {
  addBtnConfig,
  addOrEditBasicEMBtnConfig,
  AreaType,
  BasicEMBtnConfigReq,
  BasicEMDataConfigRes,
  BtnConfigActionType,
  deleteBtnConfig,
  getBasicEMDataConfig,
  getPortalBtnList,
  MajorShowBtnConfigListRes,
  StationType,
  StationTypeFormtter,
  updatePortalBtn,
  UpdatePortalBtnReq,
} from '@/api/energyAssetsPortalConfig';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Table,
  Form,
  InputNumber,
  Modal,
  PopConfirm,
  Radio,
  Button,
  EllipsisSpan,
  useUpdate,
} from '@maxtropy/components';
import { Col, Row, Space, Spin } from 'antd';
import React, { Key, useEffect, useRef, useState } from 'react';
import EditBtnModalForm, { IFieldForm } from '../EditBtnModalForm';

const columns = [
  {
    title: '序号',
    dataIndex: 'sortNum',
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '按钮名称',
    dataIndex: 'buttonName',
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: 'URL',
    dataIndex: 'url',
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => {
      return <EllipsisSpan value={v} />;
    },
  },
  {
    title: '备注',
    dataIndex: 'remark',
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v} />,
  },
];

const BasicEnergyManagementConfig = () => {
  const [form] = Form.useForm();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [topDataLoading, setTopDataLoading] = useState(false);

  const [dataSource, setDataSource] = useState<MajorShowBtnConfigListRes[]>();

  const [updateState, updateFn] = useUpdate();
  const [updateTopState, updateTopFn] = useUpdate();

  const [row, setRow] = useState<MajorShowBtnConfigListRes>();
  const [addBtnTypeModal, setAddBtnTypeModal] = useState<BtnConfigActionType>();

  const [basicEMDataConfig, setBasicEMDataConfig] = useState<BasicEMDataConfigRes>();
  const cFormRef = useRef<IFieldForm>();

  useEffect(() => {
    setLoading(true);
    getPortalBtnList({ type: AreaType.basicEMStation }).then(res => {
      setDataSource(
        res.map((i, index) => {
          return {
            ...i,
            sortNum: index + 1,
          };
        })
      );
      setLoading(false);
    });
  }, [updateState]);

  useEffect(() => {
    setTopDataLoading(true);
    getBasicEMDataConfig().then(res => {
      setBasicEMDataConfig(res);
      form.setFieldsValue({
        type: res.type,
        circuitNum: res.circuitNum,
        waterMonitorNum: res.waterMonitorNum,
        gasMonitorNum: res.gasMonitorNum,
      });
      setTopDataLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTopState]);

  // 保存上层表单
  const onFinish = (val: BasicEMBtnConfigReq) => {
    addOrEditBasicEMBtnConfig(val).then(_ => {
      updateTopFn();
      setIsEdit(false);
    });
  };

  // 重置
  const init = () => {
    setRow(undefined);
    setAddBtnTypeModal(undefined);
    cFormRef.current?.reset();
  };

  const handleEditBtn = (v: UpdatePortalBtnReq | undefined) => {
    if (v) {
      if (v.id) {
        updatePortalBtn(v).then(_ => {
          init();
          updateFn();
        });
      } else {
        addBtnConfig(v).then(_ => {
          init();
          updateFn();
        });
      }
    } else {
      init();
    }
  };

  const removeMajorArea = (id: Key) => {
    deleteBtnConfig(id).then(_ => {
      updateFn();
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      fixed: 'right' as 'right',
      width: 183,
      render: (_: undefined, record: MajorShowBtnConfigListRes) => (
        <>
          <Space size={16}>
            <PopConfirm
              title={'确定删除？'}
              onConfirm={() => removeMajorArea(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" style={{ padding: 0 }}>
                删除
              </Button>
            </PopConfirm>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                setRow(record);
                setAddBtnTypeModal(BtnConfigActionType.ADDBASICEM);
              }}
            >
              编辑
            </Button>
          </Space>
        </>
      ),
    },
  ];

  const addBasicEMBtn = () => {
    if (dataSource && dataSource?.length >= 4) {
      Modal.error({ title: '最多新建4个按钮！' });
    } else {
      setRow(undefined);
      setAddBtnTypeModal(BtnConfigActionType.ADDBASICEM);
    }
  };

  const onCancel = () => {
    form.setFieldsValue({
      type: basicEMDataConfig?.type,
      circuitNum: basicEMDataConfig?.circuitNum,
      waterMonitorNum: basicEMDataConfig?.waterMonitorNum,
      gasMonitorNum: basicEMDataConfig?.gasMonitorNum,
    });
    setIsEdit(false);
  };

  return (
    <div>
      {isEdit ? (
        <Space size={8}>
          <Button
            key="saveBasicEM"
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            保存
          </Button>
          <Button
            key="cancelBasicEM"
            onClick={() => {
              onCancel();
            }}
          >
            取消
          </Button>
        </Space>
      ) : (
        <Button
          key="editBasicEM"
          type="primary"
          onClick={() => {
            setIsEdit(true);
          }}
        >
          编辑
        </Button>
      )}
      <Spin spinning={topDataLoading}>
        <Form
          style={{ marginTop: 12 }}
          form={form}
          labelAlign="left"
          onFinish={(val: BasicEMBtnConfigReq) => onFinish(val)}
        >
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item name="type" label="站点统计数据来源" rules={[{ required: true, message: '请选择' }]}>
                <Radio.Group style={{ width: '100%' }} disabled={!isEdit}>
                  <Radio value={StationType.PlatformArea}>{StationTypeFormtter[StationType.PlatformArea]}</Radio>
                  <Radio value={StationType.ConfigArea}>{StationTypeFormtter[StationType.ConfigArea]}</Radio>
                  <Radio value={StationType.Disable}>{StationTypeFormtter[StationType.Disable]}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item noStyle dependencies={['type']}>
                {({ getFieldValue }) =>
                  getFieldValue('type') === StationType.ConfigArea && (
                    <>
                      <Form.Item name="circuitNum" label="回路数" rules={[{ required: true, message: '请输入' }]}>
                        <InputNumber
                          disabled={!isEdit}
                          style={{ width: '80%' }}
                          min={0}
                          max={99999999}
                          placeholder="请输入"
                        />
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item noStyle dependencies={['type']}>
                {({ getFieldValue }) =>
                  getFieldValue('type') === StationType.ConfigArea && (
                    <>
                      <Form.Item
                        name="waterMonitorNum"
                        label="水监测点数"
                        rules={[{ required: true, message: '请输入' }]}
                      >
                        <InputNumber
                          disabled={!isEdit}
                          style={{ width: '80%' }}
                          min={0}
                          max={99999999}
                          placeholder="请输入"
                        />
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item noStyle dependencies={['type']}>
                {({ getFieldValue }) =>
                  getFieldValue('type') === StationType.ConfigArea && (
                    <>
                      <Form.Item
                        name="gasMonitorNum"
                        label="气监测点数"
                        rules={[{ required: true, message: '请输入' }]}
                      >
                        <InputNumber
                          disabled={!isEdit}
                          style={{ width: '80%' }}
                          min={0}
                          max={99999999}
                          placeholder="开线数"
                        />
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
      <div>
        <div
          style={{
            marginBottom: 10,
          }}
        >
          <Button type="primary" onClick={() => addBasicEMBtn()}>
            新建
          </Button>
          <span style={{ marginLeft: 12 }}>
            <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)' }} />
            <span style={{ marginLeft: 4, color: 'var(--mx-text-desc-color)' }}>最多新建4个按钮</span>
          </span>
        </div>

        <Table
          scroll={{ y: 450 }}
          rowKey="id"
          loading={loading}
          className="tableScroll"
          columns={[...buildColumns]}
          dataSource={dataSource}
          pagination={false}
        />
        <EditBtnModalForm cref={cFormRef} row={row} addBtnTypeModal={addBtnTypeModal} handleEditBtn={handleEditBtn} />
      </div>
    </div>
  );
};

export default BasicEnergyManagementConfig;
